import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, renderSlot as _renderSlot, mergeProps as _mergeProps, withCtx as _withCtx, createSlots as _createSlots } from "vue"

const _hoisted_1 = { class: "app-paginator" }
const _hoisted_2 = { class: "app-paginator__right" }
const _hoisted_3 = { class: "app-paginator__report" }
const _hoisted_4 = { key: 1 }
const _hoisted_5 = ["disabled", "onClick"]
const _hoisted_6 = {
  key: 0,
  class: "app-paginator__ellipsis"
}
const _hoisted_7 = ["onClick"]
const _hoisted_8 = ["disabled", "onClick"]

import { ref, computed } from 'vue';
import DataTable, { DataTablePageEvent, DataTableSortEvent } from 'primevue/datatable';
import Select from 'primevue/select';
import Skeleton from 'primevue/skeleton';

interface Props {
    value?: unknown[];
    paginator?: boolean;
    rows?: number;
    rowsPerPageOptions?: number[];
    loading?: boolean;
    paginatorLoading?: boolean;
    dataKey?: string;
    spread?: boolean;
    totalRecords?: number;
    pageLinkSize?: number;
    lazy?: boolean;
}


export interface AppTableInstance {
    exportCurrentViewToCsv(): boolean;
}

/**
 * Column style helper — use with v-bind on PrimeVue Column.
 * Applies the same style to header, body, and col so they always match.
 *
 * @param align    - 'right' to right-align header + cells
 * @param shrink   - true → width:1px + nowrap (fit content, useful for action cols in spread mode)
 * @param fill     - true → width:100% (spacer / action col in stacked mode)
 * @param minWidth - e.g. '12rem' to set a column minimum width
 */
export function col(opts: {
    align?: 'right';
    shrink?: boolean;
    fill?: boolean;
    minWidth?: string;
} = {}) {
    const s: Record<string, string> = {};
    if (opts.align) s.textAlign = opts.align;
    if (opts.shrink) { s.width = '1px'; s.whiteSpace = 'nowrap'; }
    if (opts.fill) s.width = '100%';
    if (opts.minWidth) s.minWidth = opts.minWidth;
    return { style: { ...s }, headerStyle: { ...s }, bodyStyle: { ...s } };
}

const __default__ = { inheritAttrs: false };

export default /*@__PURE__*/_defineComponent({
  ...__default__,
  __name: 'AppTable',
  props: {
    value: { default: () => [] },
    paginator: { type: Boolean, default: true },
    rows: { default: 10 },
    rowsPerPageOptions: { default: () => [10, 25, 50] },
    loading: { type: Boolean, default: false },
    paginatorLoading: { type: Boolean, default: false },
    dataKey: { default: 'id' },
    spread: { type: Boolean, default: false },
    totalRecords: { default: 0 },
    pageLinkSize: {},
    lazy: { type: Boolean, default: false }
  },
  emits: ["page", "sort"],
  setup(__props: any, { expose: __expose }) {

/* eslint-env vue/setup-compiler-macros */
/* eslint-disable import/first, @typescript-eslint/no-explicit-any */
const props = __props;



const dt = ref<InstanceType<typeof DataTable> | null>(null);

const numberOfPages = computed(() => {
    return Math.ceil(props.totalRecords / (props.rows || 10));
});

const effectivePaginator = computed(() => {
    if (!props.paginator) return false;
    const count = props.lazy ? props.totalRecords : (props.value?.length ?? 0);
    return count > 0;
});

function getPageLinks(page: number): (number | '...')[] {
    const pageCount = numberOfPages.value;
    if (pageCount <= 1) return pageCount === 1 ? [1] : [];
    const current = page + 1;
    const visible = new Set<number>();
    visible.add(1);
    visible.add(pageCount);
    for (let i = Math.max(2, current - 1); i <= Math.min(pageCount - 1, current + 2); i += 1) {
        visible.add(i);
    }
    const sorted = [...visible].sort((a, b) => a - b);
    const result: (number | '...')[] = [];
    for (let i = 0; i < sorted.length; i += 1) {
        if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push('...');
        result.push(sorted[i]);
    }
    return result;
}

function exportCurrentViewToCsv(): boolean {
    if (!dt.value) return false;

    try {
        const dataTable = dt.value as InstanceType<typeof DataTable> & {
            dataToRender?: () => unknown[];
        };
        const exportRows = dataTable.dataToRender?.() ?? props.value ?? [];

        dt.value.exportCSV({ selectionOnly: false }, exportRows);
        return true;
    } catch {
        return false;
    }
}

__expose({
    exportCurrentViewToCsv,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(['app-table', { 'app-table--spread': __props.spread }])
  }, [
    _createVNode(_unref(DataTable), _mergeProps({
      ref_key: "dt",
      ref: dt
    }, _ctx.$attrs, {
      value: __props.value,
      paginator: effectivePaginator.value,
      rows: __props.rows,
      "rows-per-page-options": __props.rowsPerPageOptions,
      loading: __props.loading,
      "data-key": __props.dataKey,
      totalRecords: __props.totalRecords,
      lazy: __props.lazy,
      onPage: _cache[0] || (_cache[0] = (event) => _ctx.$emit('page', event)),
      onSort: _cache[1] || (_cache[1] = (event) => _ctx.$emit('sort', event))
    }), _createSlots({
      paginatorcontainer: _withCtx(({
                    first, last, page,
                    rows: currentRows,
                    prevPageCallback, nextPageCallback,
                    rowChangeCallback, changePageCallback,
                }) => [
        _createElementVNode("div", _hoisted_1, [
          _createVNode(_unref(Select), {
            "model-value": currentRows,
            options: __props.rowsPerPageOptions,
            "aria-label": "Rows per page",
            onChange: (e) => rowChangeCallback(e.value)
          }, null, 8, ["model-value", "options", "onChange"]),
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("span", _hoisted_3, [
              _cache[2] || (_cache[2] = _createTextVNode(" Showing ", -1)),
              _createElementVNode("b", null, _toDisplayString(first), 1),
              _cache[3] || (_cache[3] = _createTextVNode(" to ", -1)),
              _createElementVNode("b", null, _toDisplayString(last), 1),
              _cache[4] || (_cache[4] = _createTextVNode(" of ", -1)),
              (__props.paginatorLoading)
                ? (_openBlock(), _createBlock(_unref(Skeleton), {
                    key: 0,
                    width: "40px",
                    height: "15px",
                    "border-radius": "4px"
                  }))
                : (_openBlock(), _createElementBlock("b", _hoisted_4, _toDisplayString(__props.totalRecords), 1)),
              _cache[5] || (_cache[5] = _createTextVNode(" entries ", -1))
            ]),
            _createElementVNode("button", {
              class: "app-paginator__nav app-paginator__nav--prev",
              disabled: page === 0,
              onClick: prevPageCallback
            }, null, 8, _hoisted_5),
            (__props.paginatorLoading)
              ? (_openBlock(), _createBlock(_unref(Skeleton), {
                  key: 0,
                  width: "88px",
                  height: "15px",
                  "border-radius": "4px"
                }))
              : (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(getPageLinks(page), (link, idx) => {
                  return (_openBlock(), _createElementBlock(_Fragment, { key: idx }, [
                    (link === '...')
                      ? (_openBlock(), _createElementBlock("span", _hoisted_6, " ... "))
                      : (_openBlock(), _createElementBlock("button", {
                          key: 1,
                          class: _normalizeClass(["app-paginator__page", { 'is-active': (link as number) - 1 === page }]),
                          onClick: ($event: any) => (changePageCallback((link as number) - 1))
                        }, _toDisplayString(link), 11, _hoisted_7))
                  ], 64))
                }), 128)),
            _createElementVNode("button", {
              class: "app-paginator__nav app-paginator__nav--next",
              disabled: page === numberOfPages.value - 1,
              onClick: nextPageCallback
            }, null, 8, _hoisted_8)
          ])
        ])
      ]),
      _: 2
    }, [
      _renderList(_ctx.$slots, (_, name) => {
        return {
          name: name,
          fn: _withCtx((slotData) => [
            _renderSlot(_ctx.$slots, (name as string), _normalizeProps(_guardReactiveProps((slotData as any) ?? {})))
          ])
        }
      })
    ]), 1040, ["value", "paginator", "rows", "rows-per-page-options", "loading", "data-key", "totalRecords", "lazy"])
  ], 2))
}
}

})